/**
 * Monthly expense templates (BE-5c) — the "Monthly templates" tab of /accounting/expenses.
 * Each active template drafts a copy on its day every month; drafts wait in Pending review.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  ListSubheader, Switch, FormControlLabel, Checkbox, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, IconButton, Tooltip, useTheme, useMediaQuery, Stack, Card, CardContent,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { formatCurrency } from '../../utils/accounting/formatters';
import { StatusChip } from '../../components/accounting';
import { expenseService, encodePayFrom, decodePayFrom } from '../../services/accounting/expense.service';
import { serverMessage } from '../../utils/apiConvert';

const EMPTY = {
  categoryId: '', description: '', payeeName: '', payeeId: '', amount: '', vatAmount: '',
  vatRecoverable: false, expenseType: 'PAYABLE', payFrom: '', isTaxDeductible: true, dayOfMonth: 1, active: true,
};

const sessionTcId = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}').typingCenterId; } catch (e) { return undefined; }
};

const ExpenseTemplates = ({ categories, onError, canWrite = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payFromOptions, setPayFromOptions] = useState([]);
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null); // null closed, {} new, {id,...} edit
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await expenseService.getTemplates());
    } catch (err) {
      onError(serverMessage(err, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    expenseService.getPayFromOptions({ includeCheque: false }).then(setPayFromOptions).catch(() => {});
    expenseService.getTeamMembers(sessionTcId()).then(setTeam).catch(() => {});
  }, []);

  const isSalary = !!categories.find((c) => c.id === form.categoryId)?.isSalary;

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (t) => {
    setForm({
      categoryId: t.category?.id || '', description: t.description || '', payeeName: t.payeeName || '', payeeId: t.payeeId || '',
      amount: t.amount ?? '', vatAmount: t.vatAmount ?? '', vatRecoverable: !!t.vatRecoverable, expenseType: t.expenseType,
      payFrom: encodePayFrom(t.paymentAccountType, t.paymentAccountId), isTaxDeductible: t.isTaxDeductible !== false,
      dayOfMonth: t.dayOfMonth, active: t.active,
    });
    setEditing(t);
  };

  const toRequest = (f) => ({
    categoryId: f.categoryId,
    description: f.description.trim(),
    payeeName: isSalary ? undefined : f.payeeName,
    payeeId: isSalary ? f.payeeId : undefined,
    amount: f.amount,
    vatAmount: f.vatAmount === '' ? 0 : f.vatAmount,
    vatRecoverable: f.vatRecoverable,
    expenseType: f.expenseType,
    isTaxDeductible: f.isTaxDeductible,
    dayOfMonth: Number(f.dayOfMonth),
    active: f.active,
    ...(f.expenseType === 'PAID' ? decodePayFrom(f.payFrom) : {}),
  });

  const save = async () => {
    setBusy(true);
    try {
      if (editing?.id) await expenseService.updateTemplate(editing.id, toRequest(form));
      else await expenseService.createTemplate(toRequest(form));
      setEditing(null);
      await load();
    } catch (err) {
      onError(serverMessage(err, 'Failed to save the template'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, failMsg) => {
    try { await fn(); await load(); } catch (err) { onError(serverMessage(err, failMsg)); }
  };

  const toggleActive = (t) => act(() => expenseService.updateTemplate(t.id, {
    categoryId: t.category?.id, description: t.description, payeeName: t.payeeId ? undefined : t.payeeName,
    payeeId: t.payeeId || undefined, amount: t.amount, vatAmount: t.vatAmount, vatRecoverable: t.vatRecoverable,
    expenseType: t.expenseType, paymentAccountType: t.paymentAccountType || undefined,
    paymentAccountId: t.paymentAccountId || undefined, isTaxDeductible: t.isTaxDeductible,
    dayOfMonth: t.dayOfMonth, active: !t.active,
  }), 'Failed to update the template');

  const valid = form.categoryId && form.description.trim() && Number(form.amount) > 0
    && /^\d+(\.\d{1,2})?$/.test(String(form.amount))
    && Number(form.dayOfMonth) >= 1 && Number(form.dayOfMonth) <= 28
    && (form.expenseType !== 'PAID' || form.payFrom) && (!isSalary || form.payeeId);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === 'expenseType' && v !== 'PAID' ? { payFrom: '' } : {}) }));
  };

  const groupedPayFrom = () => {
    const by = {};
    payFromOptions.forEach((o) => { (by[o.group] = by[o.group] || []).push(o); });
    return Object.entries(by).flatMap(([g, list]) => [
      <ListSubheader key={`h-${g}`}>{g}</ListSubheader>,
      ...list.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>),
    ]);
  };

  const actions = (t) => canWrite && (
    <Box display="flex" justifyContent="flex-end" gap={0.5}>
      <Tooltip title="Edit (future months only)"><IconButton size="small" color="primary" onClick={() => openEdit(t)}><Edit /></IconButton></Tooltip>
      <Tooltip title="Delete (drafts already made stay)">
        <IconButton size="small" color="error" onClick={() => act(() => expenseService.deleteTemplate(t.id), 'Failed to delete the template')}><Delete /></IconButton>
      </Tooltip>
    </Box>
  );
  const total = (t) => formatCurrency(Number(t.amount || 0) + Number(t.vatAmount || 0));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {rows.length} templates. Each active template creates a draft on its day every month; paused templates create nothing.
          Cheques are chosen per month on the draft.
        </Typography>
        {canWrite && <Button variant="contained" startIcon={<Add />} onClick={openNew}>New Template</Button>}
      </Box>
      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">No monthly templates</Typography></Paper>
          ) : rows.map((t) => (
            <Card key={t.id} variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.95rem' }}>{t.description}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {t.category?.name}{t.payeeName ? ` · ${t.payeeName}` : ''}
                    </Typography>
                  </Box>
                  <Switch size="small" checked={!!t.active} disabled={!canWrite} onChange={() => toggleActive(t)} />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Day {t.dayOfMonth}</Typography>
                    <StatusChip type="expenseType" value={t.expenseType} />
                  </Box>
                  <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>{total(t)}</Typography>
                </Box>
                {canWrite && <Box mt={1}>{actions(t)}</Box>}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="center">Day</TableCell>
                <TableCell align="center">Type</TableCell>
                <TableCell align="right">Amount + VAT</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell sx={{ width: 120 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No monthly templates</Typography></TableCell></TableRow>
              ) : rows.map((t) => (
                <TableRow key={t.id} hover sx={{ '& td': { fontSize: '0.95rem' } }}>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.95rem">{t.description}</Typography>
                    {t.payeeName && <Typography variant="caption" color="text.secondary">{t.payeeName}</Typography>}
                  </TableCell>
                  <TableCell>{t.category?.name}</TableCell>
                  <TableCell align="center">{t.dayOfMonth}</TableCell>
                  <TableCell align="center"><StatusChip type="expenseType" value={t.expenseType} /></TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600} fontSize="0.95rem">{total(t)}</Typography>
                  </TableCell>
                  <TableCell align="center"><Switch size="small" checked={!!t.active} disabled={!canWrite} onChange={() => toggleActive(t)} /></TableCell>
                  <TableCell align="right">{actions(t)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? 'Edit monthly template' : 'New monthly template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1 }}>
            <TextField select label="Category" size="small" value={form.categoryId} onChange={set('categoryId')}>
              {categories.filter((c) => c.active !== false).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField label="Day of month (1–28)" type="number" size="small" value={form.dayOfMonth} onChange={set('dayOfMonth')} inputProps={{ min: 1, max: 28 }} />
            <TextField label="Description" size="small" value={form.description} onChange={set('description')} sx={{ gridColumn: { sm: '1 / span 2' } }} />
            {isSalary ? (
              <TextField select label="Team member" size="small" value={form.payeeId} onChange={set('payeeId')}>
                {team.map((m) => <MenuItem key={m.id} value={m.id}>{m.fullName || m.username}</MenuItem>)}
              </TextField>
            ) : (
              <TextField label="Payee" size="small" value={form.payeeName} onChange={set('payeeName')} />
            )}
            <TextField select label="Type" size="small" value={form.expenseType} onChange={set('expenseType')}>
              <MenuItem value="PAYABLE">Pay later (bill)</MenuItem>
              <MenuItem value="PAID">Paid now</MenuItem>
            </TextField>
            <TextField label="Amount (AED)" type="number" size="small" value={form.amount} onChange={set('amount')} />
            <TextField label="VAT" type="number" size="small" value={form.vatAmount} onChange={set('vatAmount')} />
            {form.expenseType === 'PAID' && (
              <TextField select label="Pay from" size="small" value={form.payFrom} onChange={set('payFrom')} sx={{ gridColumn: { sm: '1 / span 2' } }}>
                {groupedPayFrom()}
              </TextField>
            )}
          </Box>
          <FormControlLabel label="VAT recoverable" sx={{ mt: 1 }}
            control={<Checkbox size="small" checked={form.vatRecoverable} onChange={set('vatRecoverable')} disabled={!(Number(form.vatAmount) > 0)} />} />
          <FormControlLabel label="Tax deductible" sx={{ mt: 1 }}
            control={<Checkbox size="small" checked={form.isTaxDeductible} onChange={set('isTaxDeductible')} />} />
          {editing?.id && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>Changes apply to future months; drafts already made keep their values.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !valid} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseTemplates;
